(() => {
  const ROOT_SELECTOR = '[data-partnership-tabs]';
  const TAB_SELECTOR = '[data-partnership-tab]';
  const PANEL_SELECTOR = '[data-partnership-panel]';

  if (window.partnershipStoryTabsLoaded) return;
  window.partnershipStoryTabsLoaded = true;

  const controllers = new WeakMap();

  /**
   * 返回当前 Section 内的全部有效 Tab。
   *
   * @param {Element} root
   * @returns {HTMLButtonElement[]}
   */
  function getTabs(root) {
    return Array.from(root.querySelectorAll(TAB_SELECTOR));
  }

  /**
   * 激活指定 Tab，并同步面板可见性和键盘焦点顺序。
   *
   * @param {Element} root
   * @param {HTMLButtonElement} nextTab
   * @param {boolean} moveFocus
   */
  function activatePartnershipTab(root, nextTab, moveFocus) {
    const tabs = getTabs(root);
    const panels = Array.from(root.querySelectorAll(PANEL_SELECTOR));
    const activePanelId = nextTab.getAttribute('aria-controls');

    for (const tab of tabs) {
      const isActive = tab === nextTab;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    }

    for (const panel of panels) {
      panel.hidden = panel.id !== activePanelId;
    }

    if (moveFocus) nextTab.focus();
  }

  /**
   * 根据按键计算下一个需要激活的 Tab 索引。
   *
   * @param {KeyboardEvent} event
   * @param {number} currentIndex
   * @param {number} tabCount
   * @returns {number|null}
   */
  function getNextTabIndex(event, currentIndex, tabCount) {
    if (event.key === 'ArrowRight') return (currentIndex + 1) % tabCount;
    if (event.key === 'ArrowLeft') return (currentIndex - 1 + tabCount) % tabCount;
    if (event.key === 'Home') return 0;
    if (event.key === 'End') return tabCount - 1;
    if (event.key === 'Enter' || event.key === ' ') return currentIndex;
    return null;
  }

  /**
   * 处理 WAI-ARIA Tabs 键盘交互。
   *
   * @param {Element} root
   * @param {KeyboardEvent} event
   */
  function handleTabKeydown(root, event) {
    const tabs = getTabs(root);
    const currentIndex = tabs.indexOf(event.currentTarget);
    if (currentIndex < 0 || tabs.length === 0) return;

    const nextIndex = getNextTabIndex(event, currentIndex, tabs.length);
    if (nextIndex === null) return;

    event.preventDefault();
    activatePartnershipTab(root, tabs[nextIndex], true);
  }

  /**
   * 初始化单个 Tabs Section；重复调用不会绑定重复事件。
   *
   * @param {Element} root
   */
  function initializePartnershipTabs(root) {
    if (controllers.has(root)) return;

    const tabs = getTabs(root);
    if (tabs.length === 0) return;

    const controller = new AbortController();
    controllers.set(root, controller);

    for (const tab of tabs) {
      tab.addEventListener(
        'click',
        (event) => activatePartnershipTab(root, event.currentTarget, false),
        { signal: controller.signal },
      );
      tab.addEventListener('keydown', (event) => handleTabKeydown(root, event), {
        signal: controller.signal,
      });
    }

    root.classList.add('partnership-story-tabs--enhanced');
    activatePartnershipTab(root, tabs[0], false);
  }

  /**
   * 初始化指定 DOM 范围中的全部 Tabs Section。
   *
   * @param {Document|Element} scope
   */
  function initializeWithin(scope) {
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) {
      initializePartnershipTabs(scope);
    }

    for (const root of scope.querySelectorAll(ROOT_SELECTOR)) {
      initializePartnershipTabs(root);
    }
  }

  /**
   * 主题编辑器卸载 Section 时释放事件监听。
   *
   * @param {Element} scope
   */
  function cleanupWithin(scope) {
    const roots = [];
    if (scope.matches(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...scope.querySelectorAll(ROOT_SELECTOR));

    for (const root of roots) {
      controllers.get(root)?.abort();
      controllers.delete(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
  } else {
    initializeWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeWithin(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    cleanupWithin(event.target);
  });
})();
